/**
 * Meddevice compliance-notification rollout gates (GRO-3989 / MOD-1618).
 *
 * PostHog, matching the rest of Growth's new rollouts (`useIsDocxFormEnabled`).
 * These used to be Flagsmith `useBooleanFlag` reads on `@heidi/growth/flags`;
 * that door has no PostHog surface, so the getters live here and the resolved
 * booleans cross into the package as `flagGates`.
 *
 * On-prem, `posthog-compat` reads the same keys from the OpenFeature bundle
 * (`GET /feature-flag/all`). The keys were never in Flagsmith GitOps, so the
 * previous `builds: 'all'` descriptor never delivered them to the appliance
 * either — missing bundle entries still resolve OFF. Cloud rollout is the
 * PostHog flags; appliance delivery is a bundle-seed, not a provider swap.
 *
 * Leaf module rather than the `index.ts` barrel so the keys have one minting
 * site and a suite can mock `posthog-compat` without evaluating the whole
 * barrel. Kill switch / rollout, not an experiment — no `useManualExposure`.
 * Default OFF.
 */
import { useFeatureFlagEnabled } from '@/features/flags/posthog-compat';

export const MEDDEVICE_MFA_SETUP_NOTIFICATION_FLAG_KEY =
  'release-scribe-notification-mfa-setup';

export const MEDDEVICE_INACTIVITY_LOGOUT_NOTIFICATION_FLAG_KEY =
  'release-scribe-notification-inactivity-logout';

/** The pinned MFA-setup compliance recommendation. */
export const useIsMeddeviceMfaSetupNotificationEnabled = (): boolean =>
  useFeatureFlagEnabled(MEDDEVICE_MFA_SETUP_NOTIFICATION_FLAG_KEY) ?? false;

/** The pinned inactivity sign-out compliance recommendation. */
export const useIsMeddeviceInactivityLogoutNotificationEnabled = (): boolean =>
  useFeatureFlagEnabled(MEDDEVICE_INACTIVITY_LOGOUT_NOTIFICATION_FLAG_KEY) ?? false;

/**
 * Both gates as the package seam expects them. One mapping site so composition
 * points cannot swap MFA onto the inactivity field.
 */
export const useComplianceNotificationFlagGates = (): {
  mfaSetup: boolean;
  inactivityLogout: boolean;
} => ({
  mfaSetup: useIsMeddeviceMfaSetupNotificationEnabled(),
  inactivityLogout: useIsMeddeviceInactivityLogoutNotificationEnabled(),
});
